import { useEffect, useState } from 'react';

/**
 * Whether a CSS media query currently matches, kept in step as the window changes.
 *
 * The header needs this in JavaScript rather than CSS because collapsing its icons is not a
 * restyle: the same control has to RENDER differently - a bare icon with a tooltip when there is
 * room, a labelled row inside a menu when there is not. Doing that with `display:none` would mean
 * two copies of every button in the DOM, two sets of state, and a tooltip on a control nobody can
 * see. So the breakpoint is read once and the right one is built.
 *
 * Initialised from a real match rather than `false`, so a narrow window does not render the wide
 * header for a frame and then snap.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    // The query itself can change between renders, so re-read before listening.
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * The width below which the header stops showing every tool at once. Named rather than repeated:
 * the value appears in the component AND in the stylesheet that sizes what is left, and those two
 * drifting apart is how a control ends up half-collapsed.
 */
export const HEADER_COMPACT = '(max-width: 720px)';
